










































































































































import Vue from 'vue';

import DOM from '@/utils/DOM';
import Str from '@/utils/Str';
import { getQueryParameter, updateQueryParameters } from '@/utils/location';

import Movie from '@/models/soukai/Movie';

import { SearchIndex } from '@/services/Media';

import { MenuOption } from '@/components/base/BaseMenu.vue';
import MarkdownContent from '@/components/MarkdownContent.vue';
import ImportMediaModal from '@/components/modals/ImportMediaModal.vue';
import MoviesGrid from '@/components/MoviesGrid.vue';

const enum WatchedFilter {
    All = 'all',
    Watched = 'watched',
    WatchLater = 'watch-later',
}

interface WatchFilterMenuOptions extends MenuOption {
    watchedFilter: WatchedFilter;
    mobileText: string;
}

interface Data {
    actionsMenuOpen: boolean;
    watchedFilter: WatchedFilter;
    searchFilter: string | null;
    showGoTop: boolean;
    removeClickAwayListener: Function | true | null;
    scrollListener: any | null;
    keyboardListener: any | null;
}

function hasScrolled(): boolean {
    return document.body.scrollTop > 370 || document.documentElement.scrollTop > 370;
}

export default Vue.extend({
    components: {
        MoviesGrid,
        MarkdownContent,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            default: null,
        },
        searchIndex: {
            type: Array as () => SearchIndex,
            required: true,
        },
    },
    data: (): Data => ({
        actionsMenuOpen: false,
        showGoTop: hasScrolled(),
        watchedFilter: WatchedFilter.All,
        searchFilter: null,
        removeClickAwayListener: null,
        scrollListener: null,
        keyboardListener: null,
    }),
    computed: {
        watchedFilterOptions(): WatchFilterMenuOptions[] {
            const handle = ({ watchedFilter }: WatchFilterMenuOptions) => {
                this.watchedFilter = watchedFilter;
            };

            return [
                {
                    watchedFilter: WatchedFilter.All,
                    text: 'All movies',
                    mobileText: 'All',
                    icon: 'view-list',
                    handle,
                },
                {
                    watchedFilter: WatchedFilter.Watched,
                    text: 'Watched movies',
                    mobileText: 'Watched',
                    icon: 'checkmark',
                    handle,
                },
                {
                    watchedFilter: WatchedFilter.WatchLater,
                    text: 'Movies to watch later',
                    mobileText: 'Watch later',
                    icon: 'time',
                    handle,
                },
            ];
        },
        watchedFilterText(): string {
            return this.watchedFilterOptions.find(option => option.watchedFilter === this.watchedFilter)!.text;
        },
        watchedFilterMobileText(): string {
            return this.watchedFilterOptions.find(option => option.watchedFilter === this.watchedFilter)!.mobileText;
        },
        searching(): boolean {
            return this.removeClickAwayListener !== null;
        },
        filteredMovies(): Movie[] {
            let filteredIndex = this.searchIndex;

            switch (this.watchedFilter) {
                case WatchedFilter.Watched:
                    filteredIndex = filteredIndex.filter(entry => entry.movie.watched);
                    break;
                case WatchedFilter.WatchLater:
                    filteredIndex = filteredIndex.filter(entry => !entry.movie.watched);
                    break;
            }

            if (this.searching) {
                const filterText = Str.slug(this.searchFilter!, '');

                filteredIndex = filteredIndex.filter(entry => entry.searchableText.includes(filterText));
            }

            return filteredIndex.map(entry => entry.movie);
        },
        collectionQualifier(): string {
            return this.$viewer.isActive ? 'the' : 'your';
        },
    },
    watch: {
        searchFilter(searchFilter: string) {
            updateQueryParameters({ search: searchFilter && searchFilter.length > 0 ? searchFilter : undefined });
        },
        watchedFilter(watchedFilter: WatchedFilter) {
            updateQueryParameters({ status: watchedFilter === WatchedFilter.All ? undefined : watchedFilter });
        },
    },
    created() {
        this.searchFilter = getQueryParameter('search');
        this.watchedFilter = getQueryParameter('status') as WatchedFilter ?? WatchedFilter.All;

        if (this.searchFilter) {
            this.removeClickAwayListener = true;
        }
    },
    mounted() {
        if (this.removeClickAwayListener === true) {
            const input = this.$refs.searchFilter as HTMLInputElement;
            const trigger = (this.$refs.filtersTrigger as Vue).$el as HTMLButtonElement;

            this.removeClickAwayListener = this.$ui.onClickAway(
                [input, trigger],
                () => this.searchFilter!.length === 0 && this.hideFilters(),
            );
        }

        document.addEventListener('scroll', this.scrollListener = () => this.showGoTop = hasScrolled());
        document.addEventListener('keydown', this.keyboardListener = (event: KeyboardEvent) => {
            if (!this.captureHotKey(event))
                return;

            event.preventDefault();
        });
    },
    destroyed() {
        if (this.scrollListener) {
            document.removeEventListener('scroll', this.scrollListener);
            this.scrollListener = null;
        }

        if (this.keyboardListener) {
            document.removeEventListener('keydown', this.keyboardListener);
            this.keyboardListener = null;
        }
    },
    methods: {
        captureHotKey({ target, key }: KeyboardEvent): boolean {
            if (key.toLowerCase() !== 'f' || DOM.isWritable(target))
                return false;

            this.showFilters();

            return true;
        },
        showFilters() {
            const input = this.$refs.searchFilter as HTMLInputElement;
            const trigger = (this.$refs.filtersTrigger as Vue).$el as HTMLButtonElement;

            if (this.removeClickAwayListener !== null) {
                input.focus();

                return;
            }

            this.searchFilter = '';
            this.removeClickAwayListener = this.$ui.onClickAway(
                [input, trigger],
                () => this.searchFilter!.length === 0 && this.hideFilters(),
            );

            this.$nextTick(() => input.focus());
        },
        hideFilters() {
            if (this.removeClickAwayListener === null) {
                return;
            }

            if (typeof this.removeClickAwayListener === 'function') {
                this.removeClickAwayListener();
            }

            this.searchFilter = null;
            this.removeClickAwayListener = null;
        },
        importMedia() {
            this.$ui.openModal(ImportMediaModal);
        },
        exportCollection() {
            this.$media.exportCollection();
        },
        goToTop() {
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        },
    },
});
